import React, { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import Actions from "../../../../components/actionbar";
import Panel from "../../components/animation/panel";

import subscriptionPlanService from "src/services/subscriptionPlan.service";

import TimeAgo from "react-timeago";
import { FileService } from "src/services/file.service";
import Truncate from "src/components/truncate";
import Tooltip from "src/components/tooltip";

import buildUrl from "build-url-ts";
import AuthService from "src/services/auth.service";
import userRoleService from "src/services/userRoles.service.js";
import AssetService from "src/services/asset.service";
import {useSearchParams, useLocation, useNavigate} from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchPreviewAsset } from "src/redux/actions/assets";
import { animated, useTransition } from "@react-spring/web";
import EventService from 'src/services/event.service';

import { useAssetAdoption } from "src/redux/actions/adoption/asset";
import { generateLink } from "src/redux/actions/link";
import { useSeekAdoption } from "src/redux/actions/adoption/seek";
import { ASSET_DETAIL } from "src/constants/adoption/sourceTypes";

import _ from "lodash";
import cx from "classnames"
import styles from "./header.module.scss";

const WordIcon = '/images/microsoftWopiIntegration/word.png'
const PowerpointIcon = '/images/microsoftWopiIntegration/powerpoint.png'
const ExcelIcon = '/images/microsoftWopiIntegration/excel.png'
const GoogleSheetsIcon = '/images/google/googleSheets.png'
const GoogleDocsIcon = '/images/google/googleDocs.png'
const GoogleSlideIcon = '/images/google/googleSlides.png'
const Seek = '/images/seek.gif'
const PaperfliteLogo = '/images/paperfliteLogo.svg';

interface HeaderProps {
  showMenubar: boolean;
  toggleShowAnalytics: () => void;
  toggleEditAsset: () => void;
  toggleGenerateLink: () => void;
  toggleCloneAsset: () => void;
  toggleAddToCollection: () => void;
  toggleCompose: () => void;
  toggleSeek: () => void;
  toggleRecommendedAssets: (flag: boolean) => void;
  toggleShareSettings: () => void;
  handleLinkedInClick: () => void;
  isLinkedInIntegrationActive: boolean;
  isLiteAssetView: boolean;
}

const Header = ({
  showMenubar,
  toggleShowAnalytics,
  toggleEditAsset,
  toggleGenerateLink,
  toggleCloneAsset,
  toggleAddToCollection,
  toggleCompose,
  toggleSeek,
  toggleRecommendedAssets,
  toggleShareSettings,
  handleLinkedInClick,
  isLinkedInIntegrationActive,
  isLiteAssetView
}: HeaderProps) => {

  const location = useLocation();
  const navigate = useNavigate();

  const asset = useSelector((state: any) => _.get(state, "asset.item", null));

  const searchParams = new URLSearchParams(location.search);
  const source = searchParams.get("source");

  var url = "";
  if (source && EventService.shouldBackToSource(source)) {
    url = `/${source.startsWith('/') ? source.slice(1) : source}`;
  } else {
    url = `/hub`;
  }

  return (
    <div className={styles.container}>
      <div className={styles.left}>
      {
        isLiteAssetView ? (
          <img className={styles.paperfliteLogo} src={PaperfliteLogo}/>        
        ) : (
          <i
            className={cx("paperflite-left-arrow", styles.backArrow)}
            onClick={() => navigate(url)}
          />
        )
      }
        <div className={styles.details}>
          <AssetDetails asset={asset} />
        </div>
      </div>
      <div className={styles.right}>
      {
        isLiteAssetView ? (
          <LiteAppHeaderMenu
            toggleShowAnalytics={toggleShowAnalytics}
          />
        ) : (
          <HeaderMenuBarActions
            showMenubar={showMenubar}
            toggleEditAsset={toggleEditAsset}
            toggleShowAnalytics={toggleShowAnalytics}
            toggleGenerateLink={toggleGenerateLink}
            toggleCloneAsset={toggleCloneAsset}
            toggleAddToCollection={toggleAddToCollection}
            toggleCompose={toggleCompose}
            toggleSeek={toggleSeek}
            toggleRecommendedAssets={toggleRecommendedAssets}
            toggleShareSettings={toggleShareSettings}
            handleLinkedInClick={handleLinkedInClick}
            isLinkedInIntegrationActive={isLinkedInIntegrationActive}
          />
        )
      }
      </div>
    </div>
  );
};

export default Header;

interface HeaderMenuBarLiteViewProps{
  toggleShowAnalytics: () => void;
}

const LiteAppHeaderMenu = ({ toggleShowAnalytics } : HeaderMenuBarLiteViewProps) => {
  const asset = useSelector((state: any) => _.get(state, "asset.item", null));

  const isExternalAsset = () => {
    return _.get(asset, "externalMetadata", false);
  };

  const handleMenuItemClick = (action: string) => {
    if (action === "show_analytics") {
      toggleShowAnalytics()
    }
  }

  const handleClose = () => {
    const closeModal = {
      closeModal: true,
    };
    window.parent.postMessage(closeModal, "*");
  }

  return (
    <div className={cx(styles.headerMenuBar)}>
      <Actions.Container>
        <Actions.Row>
          {!isExternalAsset() && (
            <Actions.IconButton
              icon="paperflite-analytics"
              tooltip="Analytics"
              onClick={() => {
                handleMenuItemClick("show_analytics")
              }}
            />
          )}
          <Actions.IconButton
            icon="paperflite-close"
            tooltip="Close"
            onClick={handleClose}
          />
        </Actions.Row>
      </Actions.Container>
    </div>
  )
}

interface HeaderMenuBarActionsProps {
  showMenubar: boolean;
  toggleShowAnalytics: () => void;
  toggleEditAsset: () => void;
  toggleGenerateLink: () => void;
  toggleCloneAsset: () => void;
  toggleAddToCollection: () => void;
  toggleCompose: () => void;
  toggleSeek: () => void;
  toggleRecommendedAssets: (flag: boolean) => void;
  toggleShareSettings: () => void;
  handleLinkedInClick: () => void;
  isLinkedInIntegrationActive: boolean;
}

const HeaderMenuBarActions = ({
  showMenubar,
  toggleShowAnalytics,
  toggleEditAsset,
  toggleGenerateLink,
  toggleCloneAsset,
  toggleAddToCollection,
  toggleCompose,
  toggleSeek,
  toggleRecommendedAssets,
  toggleShareSettings,
  handleLinkedInClick,
  isLinkedInIntegrationActive
}: HeaderMenuBarActionsProps) => {
  const {
    trackAssetShareClickedEvent,
    trackAssetDownloadEvent,
    trackGenerateLinkClicked,
    trackAssetCloneClicked,
    trackAddCollectionClicked,
    trackEditAssetClicked,
    trackAssetAnalyticsClicked,
    trackAddPagesClicked,
    trackDocumentClicked,
    trackAssetDownloadedEvent,
  } = useAssetAdoption();

  const { trackSeekClicked } = useSeekAdoption();
  const asset = useSelector((state: any) => _.get(state, "asset.item", null));
  const fileType = FileService.getFileType(asset.metadata);

  const microsoftEditAccessEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.microsoftEditAccessEnabled", false),
  );
  const socialShareEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.socialShareEnabled", false),
  );

  const accountSeekEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.seekEnabled", false),
  );

  const accountEmployerAdvocacyEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.employerAdvocacyEnabled", false),
  );

  const collection = useSelector((state: any) => _.get(state, "collecttions.collection", null));

  const canShare = asset.settings && asset && userRoleService.canShare() && !asset.externalMetadata && asset.settings.share;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [show, setShow] = useState(false);
  const [seekEnabled, setSeekEnabled] = useState(false);
  const [socialMedium, setSocialMedium] = useState("");

  const [searchParams] = useSearchParams();

  const socialLinkGenerated = useSelector((state: any) => {
    const creating = _.get(state, 'link.generateLink.creating');
    if (creating) {
      return null;
    }

    return _.get(state, 'link.generateLink.link');
  });

  useEffect(() => {
    if (accountSeekEnabled) {
      const fileType = FileService.getFileType(_.get(asset, "metadata"));
      const supportedFileTypes = [
        "PPTX",
        "PDF",
        "DOCX",
        "DOC",
        "PPT",
        "WORD",
        "LINK",
        "VIDEO",
        "G-DOC",
        "G-SLIDES",
      ];
      if (
        (_.includes(supportedFileTypes, fileType) ||
          _.includes(_.get(asset, "metadata.content_type"), "video/")) &&
        asset.seek
      ) {
        setSeekEnabled(true);
      }
    }
  }, [asset]);

  const handleDownloadButtonClick = () => {
    if (!asset) {
      return false;
    }

    const url = AuthService.getURL(`api/2.0/assets/${asset.id}/download`, {
      modifiedDate: asset.metadata.modified_date,
      _: new Date().getTime(),
    });

    if (url) {
      window.open(url, "_blank");

      var fileType = FileService.getFileType(_.get(asset,"metadata"))
      const querySource = searchParams.get("source");
      const source = querySource && querySource.split('/');
      const collectionName = (source && source[0] == "collections") ? _.get(collection, `[${source[1]}].item.name`) : "";
      trackAssetDownloadedEvent({collectionName: collectionName, assetType: fileType});
    }
  };

  const handleMenuItemClick = (action: string) => {
    if (show) {
      setShow(!show);
    }

    if (action === "download") {
      trackAssetDownloadEvent({assetType: fileType});
      handleDownloadButtonClick();
    }

    if (action === "edit_asset") {
      trackEditAssetClicked({assetType: fileType});
      toggleEditAsset();
    }

    if (action === "show_analytics") {
      trackAssetAnalyticsClicked({assetType: fileType});
      toggleShowAnalytics();
    }

    if (action === "show_generateLink") {
      trackGenerateLinkClicked({assetType: fileType, sourceCta: ASSET_DETAIL});
      toggleGenerateLink();
    }

    if (action === "clone_asset") {
      trackAssetCloneClicked({assetType: fileType});
      toggleCloneAsset();
    }

    if (action === "add_to_collection") {
      trackAddCollectionClicked({assetType: fileType});
      toggleAddToCollection();
    }

    if (action === "add_pages") {
      trackAddPagesClicked();
      navigate(`/assets/update-asset/${asset.id}`);
      dispatch(fetchPreviewAsset({ id: asset.id }));
    }

    if (action === "show_microsoft_online") {
      const protocol = `${window.location.protocol}`;

      const url = buildUrl(protocol, {
        path: `/assets/office/${asset.id}`,
      });
      
      trackDocumentClicked();
      window.open(url, "_blank");
    }

    if (action === "show_gdocs_online") {
      const protocol = `${window.location.protocol}`;

      let url = buildUrl(protocol, {
        path: `/assets/gdoc/${asset.id}`,
      });

      if (
        fileType === "G-DOC" ||
        fileType === "G-SHEET" ||
        fileType === "G-SLIDES"
      ) {
        url = asset.metadata.gsuite_alternate_link;
      }

      trackDocumentClicked();
      window.open(url, "_blank");
    }

    if (action === "share_settings") {
      toggleShareSettings();
    }
  };

  const isExternalAsset = () => {
    return _.get(asset, "externalMetadata", false);
  };

  const isCleverstoryAsset = () => {
    return _.get(asset, "viewer", "") === "cleverstory_viewer";
  };

  const canShowDownload = () => {
    if (isCleverstoryAsset()) {
      return false;
    }

    if (asset.metadata.content_type === "application/url") {
      return false;
    }

    if (asset.viewer === "gsuite_viewer") {
      return false;
    }

    if (!asset.settings.download) {
      return false;
    }

    if (!userRoleService.canDownload()) {
      return false;
    }

    return true;
  };

  const canAccessMicrosoftOnline = () => {
    if (!subscriptionPlanService.canAccessNativeEditor()) {
      return false;
    }

    if (asset && asset.canEdit) {
      const filesize = asset.metadata.content_length;
      const fileType = FileService.getFileType(asset.metadata);
      const extension = asset.metadata.extension;

      if (
        extension &&
        (extension.toUpperCase() === "WOPITEST" ||
          extension.toUpperCase() === "WOPITESTX")
      ) {
        return true;
      } else if (fileType === "XLSX" && filesize < 5000000) {
        return true;
      } else if (fileType === "DOCX" && filesize < 50000000) {
        return true;
      } else if (fileType === "PPTX" && filesize < 300000000) {
        return true;
      } else if (fileType === "G-DOC") {
        return true;
      } else if (fileType === "G-SLIDES") {
        return true;
      } else if (fileType === "G-SHEET") {
        return true;
      }
    }
    return false;
  };

  const renderMicrosofDocMenuItem = () => {
    let microsoftDocTitle = "";
    let microsoftDocIcon = "";

    const fileType = FileService.getFileType(asset.metadata);
    const extension = asset.metadata.extension;
    if (
      extension &&
      (extension.toUpperCase() === "WOPITEST" ||
        extension.toUpperCase() === "WOPITESTX")
    ) {
      microsoftDocTitle = "Wopi Validation Service";
    } else if (fileType === "XLSX") {
      microsoftDocTitle = "Microsoft Excel";
      microsoftDocIcon = ExcelIcon;
    } else if (fileType === "DOCX") {
      microsoftDocTitle = "Microsoft Word";
      microsoftDocIcon = WordIcon;
    } else if (fileType === "PPTX") {
      microsoftDocTitle = "Microsoft Powerpoint";
      microsoftDocIcon = PowerpointIcon;
    }
    if (!microsoftDocTitle) {
      return null;
    }

    return (
      <Actions.DropdownMenuItem
        icon={microsoftDocIcon}
        text={microsoftDocTitle}
        isLogo={true}
        onClick={() => handleMenuItemClick("show_microsoft_online")}
      />
    );
  };

  const renderGdocsMenuItem = () => {
   
    if(asset){
      var fileType = FileService.getFileType(asset.metadata);

      if(!asset.source){
        return showGDocOptions(fileType);
      }else if(asset.source.trim().toUpperCase() === "GOOGLE DRIVE"){
        if(fileType === 'G-SHEET' || fileType === 'G-DOC' || fileType === 'G-SLIDES'){
          return showGDocOptions(fileType);
        }
      }
    }
  }

  const showGDocOptions = (fileType) => {

    let title = "";
    let icon = "";

    if (fileType === "XLSX" || fileType === "G-SHEET") {
      title = "Google Sheets";
      icon = GoogleSheetsIcon;
    } else if (fileType === "DOCX" || fileType === "G-DOC") {
      title = "Google Docs";
      icon = GoogleDocsIcon;
    } else if (fileType === "PPTX" || fileType === "G-SLIDES") {
      title = "Google Slides";
      icon = GoogleSlideIcon;
    }
    if (!title) {
      return null;
    }

    return (
      <Actions.DropdownMenuItem
        icon={icon}
        text={title}
        isLogo={true}
        onClick={() => handleMenuItemClick("show_gdocs_online")}
      />
    );
  };

  const canAddPages = useMemo(() => {
    if (!asset) {
      return false;
    }

    if (asset.source) {
      return false;
    }

    const extension = _.get(asset, "metadata.extension");
    if (extension != "pdf" && extension != "pptx") {
      return false;
    }

    const viewer = asset.viewer;
    if (viewer != "enhanced_viewer") {
      return false;
    }
    return true;
  }, [asset.id]);

  const [transitions, api] = useTransition(showMenubar, () => ({
    initial: { opacity: 0, top: "0px" },
    from: { opacity: 1, top: "-90px" },
    enter: { opacity: 1, top: "0px" },
    leave: { opacity: 0, top: "-90px" },
    // config: { duration: 500 },
    // delay: 500,
  }));

  useEffect(() => {
    api.start();
  }, [showMenubar]);

  const handleShareClick = (medium) => {
    trackAssetShareClickedEvent({assetType: fileType, shareMedium: medium});
    toggleCompose();
  }

  const handleSocialShare = (medium) => {
    setSocialMedium(medium);
    dispatch(generateLink({
      entityID: asset.id!,
      entityType: 'asset',
      type: 'anonymous'
    }))
  }

  const handleLinkedInShareClick = () => {
    if (accountEmployerAdvocacyEnabled) {
      trackAssetShareClickedEvent({ assetType: fileType, shareMedium: "Linkedin" });
      handleLinkedInClick();
      return;
    }
    handleSocialShare("linkedin");
  }

  const handleSocialLinkGenerated = (link, type) => {
    let url = link.url;

    const queryParams = {
      _: new Date().getTime()
    };

    const utmCampaign = _.omitBy(link.utmCampaign, _.isNil);
    _.forOwn(utmCampaign, (value, key) => {
      queryParams[`utm_${key}`] = value
    });

    url = buildUrl(url, {
      queryParams: queryParams
    });

    url = encodeURI(url);

    const winTop = (window.screen.height - 500) / 2;
    const winLeft = (window.screen.width - 575) / 1.75;
    const collectionName = _.get(collection, 'name', '');

    let shareUrl: string | null = null;
    switch (type) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer.php?u=${url}&title=${collectionName}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${collectionName}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/share?url=${url}&title=${collectionName}`;
        break;
    }

    if (shareUrl) {
      trackAssetShareClickedEvent({assetType: fileType, shareMedium: _.capitalize(socialMedium)});
      window.open(shareUrl, 'sharer', 'top=' + winTop + ',left=' + winLeft + ',toolbar=0,status=0,width=' + 500 + ',height=' + 575);
    }
  }

  useEffect(() => {
    if(socialLinkGenerated) {
      handleSocialLinkGenerated(socialLinkGenerated, socialMedium);
    }
  }, [socialLinkGenerated]);

  return transitions(
    (style, item) =>
      item && (
        <animated.div style={style} className={cx(styles.headerMenuBar)}>
          <Actions.Container>
              <Actions.Row>
                {seekEnabled && (
                  <Tooltip content="seek" placement="bottom">
                  <div
                    onClick={() => {
                      toggleSeek()
                      trackSeekClicked({ assetType: fileType, sourceCta: ASSET_DETAIL }); 
                    }}
                    className={styles.seekContainer}
                  >
                    <img src={Seek} />
                  </div>
                  </Tooltip>
                )}
                {canShare && socialShareEnabled && (
                  <Actions.Group icon="paperflite-share">
                    <Actions.IconButton
                      icon="paperflite-mail"
                      tooltip="Compose"
                      onClick={ () => handleShareClick("Email")} 
                    />
                    <Actions.IconButton icon="paperflite-facebook" tooltip="Facebook" onClick={() => handleSocialShare('facebook')} />
                    <Actions.IconButton icon="paperflite-twitter" tooltip="Twitter" onClick={() => handleSocialShare('twitter')} />
                    <Actions.IconButton icon="paperflite-linkedIn" tooltip="Linkedin" onClick={() => handleLinkedInShareClick()} /> 
                  </Actions.Group>
                )}

                {canShare && !socialShareEnabled && (
                  <Actions.IconButton
                    icon="paperflite-share"
                    tooltip="Share"
                    onClick={() => handleShareClick("Email")} 
                  />
                )}

                {!isExternalAsset() && canShare && (
                  <Actions.IconButton
                    icon="paperflite-link"
                    tooltip="Generate Link"
                    onClick={() => handleMenuItemClick("show_generateLink")}
                  />
                )}
                {!isExternalAsset() && (
                  <Actions.IconButton
                    icon="paperflite-analytics"
                    tooltip="Analytics"
                    onClick={() => {
                      handleMenuItemClick("show_analytics")
                    } }
                  />
                )}

                {canShowDownload() && (
                  <Actions.IconButton
                    icon="paperflite-download"
                    tooltip="Download"
                    onClick={() => handleMenuItemClick("download")}
                  />
                )}
                {!isExternalAsset() && (
                  <Actions.Dropdown>
                    <Actions.DropdownToggle
                      icon="paperflite-more"
                      onToggle={() => {
                        toggleRecommendedAssets(show)
                        setShow(!show)}
                    }
                    />
                    <Panel show={show} className={styles.menuContainer}>
                      <div className={styles.menuContainer}>
                        <Actions.DropdownMenu
                          show={show}
                          className={styles.menu}
                        >
                          <div className={styles.headingContent}>
                            <h3 className={styles.header}>more options:</h3>
                            <i
                              onClick={() => {
                                toggleRecommendedAssets(show)
                                setShow(!show)}
                              }
                              className="paperflite-close"
                            />
                          </div>
                          {asset.canEdit && (
                            <Actions.DropdownMenuItem
                              icon="paperflite-edit"
                              text="Edit asset"
                              className={styles.menuItem}
                              onClick={() => handleMenuItemClick("edit_asset")}
                            />
                          )}

                          {AssetService.canClone(asset) &&
                            !isCleverstoryAsset() && (
                              <Actions.DropdownMenuItem
                                icon="paperflite-clone"
                                className={styles.menuItem}
                                text="Clone asset"
                                onClick={() =>
                                  handleMenuItemClick("clone_asset")
                                }
                              />
                            )}

                          {userRoleService.canAccessCollection() && (
                            <Actions.DropdownMenuItem
                              icon="paperflite-add"
                              text="Add to collection"
                              className={styles.menuItem}
                              onClick={() =>
                                handleMenuItemClick("add_to_collection")
                              }
                            />
                          )}

                          {userRoleService.canCreateAsset() && canAddPages && (
                            <Actions.DropdownMenuItem
                              icon="paperflite-add"
                              text="Add Pages"
                              className={styles.menuItem}
                              onClick={() => handleMenuItemClick("add_pages")}
                            />
                          )}

                          {!asset.source &&
                            microsoftEditAccessEnabled &&
                            canAccessMicrosoftOnline() &&
                            renderMicrosofDocMenuItem()}

                          {canAccessMicrosoftOnline() && renderGdocsMenuItem()}

                        {asset.canEdit && !isExternalAsset() && accountEmployerAdvocacyEnabled && <Actions.DropdownMenuItem
                          icon="paperflite-linkedIn"
                          text="Share settings"
                          className={styles.menuItem}
                          onClick={() => handleMenuItemClick("share_settings")}
                        />}

                        </Actions.DropdownMenu>
                      </div>
                    </Panel>
                  </Actions.Dropdown>
                )}
              </Actions.Row>
          </Actions.Container>
        </animated.div>
      ),
  );
};

interface AssetDetailsProps {
  asset: any;
}

const AssetDetails = ({ asset }: AssetDetailsProps) => {
  const padNumber = (number = 0, pad) => {
    number = Math.ceil(number);

    const length = pad - String(number).length + 1;
    let padding = "";
    if (length > 0) {
      padding = "0".repeat(length);
    }

    return padding + number;
  };

  const formatVideoDuration = (timeSpent) => {
    let timeSpentValue = `${padNumber(timeSpent, 1)} seconds`;

    if (timeSpent >= 60) {
      const minutes = Math.round(timeSpent / 60);
      timeSpentValue = `${padNumber(minutes, 1)} minute${minutes === 1 ? "" : "s"}`;
    }

    return timeSpentValue;
  };

  const isExpiryInOneWeek = (expiryDate) => {
    const currentDate = new Date();
    const oneWeekLaterDate = new Date();
    oneWeekLaterDate.setDate(currentDate.getDate() + 7);
    return expiryDate <= oneWeekLaterDate;
  };
  const isExpired = _.get(asset, "expiry", false);
  const expiryDate =
    asset && asset.expiryDate ? new Date(asset.expiryDate) : "";
  const isExpiryInOneWeekFlag = expiryDate && isExpiryInOneWeek(expiryDate);

  let additionalData: React.JSX.Element | null = null;
  if (asset.metadata.pages) {
    additionalData = (
      <>
        <span className={styles.content}>
          {asset.metadata.pages} pages
        </span>
        <div className={styles.line}></div>
      </>

    );
  } else if (asset.metadata.video_duration) {
    additionalData = (
      <span className={styles.content}>
        <label>{formatVideoDuration(asset.metadata.video_duration)}</label>
        <div className={styles.line}></div>
      </span>
    );
  }

  const containsExternalLink = (asset) => {
    if (_.get(asset, "metadata.gsuite_web_view_url") || isCleverstoryAsset()) {
      return true;
    }
    return false;
  };

  const isCleverstoryAsset = () => {
    return _.get(asset, "viewer", "") === "cleverstory_viewer";
  };

  const getAssetExpiry = () => {
    const expiryDate = new Date(asset.expiryDate);
    const isExpiryInOneWeek = (expiryDate) => {
      const currentDate = new Date();
      const oneWeekLaterDate = new Date();
      oneWeekLaterDate.setDate(currentDate.getDate() + 7);
      return expiryDate <= oneWeekLaterDate;
    };
    const isExpired = _.get(asset, "expiry", false);
    const expiryInOneWeek = isExpiryInOneWeek(expiryDate);

    return (
      <span>
        {isExpired ? (
          <span className={styles.assetExpired}>Expired</span>
        ) : (
          expiryInOneWeek && (
            <span>
              <span className={styles.assetExpiring}>Expiring</span>
            </span>
          )
        )}
      </span>
    );
  };
  return (
    <div className={styles.details}>
      <div className={styles.assetNameWrapper}>
        <Tooltip placement="right" content={asset.name}>
          <span>
            <Truncate width={700} lines={1} className={styles.title}>
              {asset.name}
            </Truncate>
          </span>
        </Tooltip>
        {expiryDate && getAssetExpiry()}
      </div>
      <div className={styles.metadata}>
        <span className={styles.content}>
          {FileService.getFileType(asset.metadata)}
        </span>
        <div className={styles.line}></div>
        {additionalData}

        {asset.metadata.content_length > 0 && (
          <>
            <span className={styles.content}>
              {FileService.formatSize(asset.metadata.content_length)}
            </span>
            <div className={styles.line}></div>
          </>
        )}

        <span className={styles.content}>
          {containsExternalLink(asset) ? (
            <a
              href={
                _.get(asset, "metadata.gsuite_web_view_url") ||
                _.get(asset, "metadata.editor_url")
              }
              className={styles.gsuiteLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {asset.source || "Paperflite Cloud"}
            </a>
          ) : (
            <label>{asset.source || "Paperflite Cloud"}</label>
          )}
        </span>

        {!isExpired &&
          isExpiryInOneWeekFlag &&
          _.get(asset, "expiryDate", false) && (
            <>
              <div className={styles.line}></div>
              <span className={styles.content}>
                Expiring on <TimeAgo date={expiryDate} locale="en-US" />
              </span>
            </>
          )}
      </div>
    </div>
  );
};
